import { Component, Inject, OnInit } from '@angular/core';
import { MODAL_DATA, NgbActiveModal } from '@agilent/ui-element';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import IpSubnetCalculator from 'ip-subnet-calculator';
import Utils from '@app/utils/utils';

@Component({
  selector: 'app-ac-nic-config-dialog',
  templateUrl: './ac-nic-config-dialog.component.html',
  styleUrls: ['./ac-nic-config-dialog.component.scss']
})
export class AcNicConfigDialogComponent implements OnInit {

  nicName: string;
  nicInfo: any;
  nicForm: FormGroup;
  method: string;
  isMandatory = true;

  constructor(
    @Inject(MODAL_DATA) public modalData: any,
    private activeModal: NgbActiveModal,
    private formBuilder: FormBuilder
  ) {
    //
  }

  ngOnInit() {
    this.nicName = this.modalData.nicName;
    this.nicInfo = this.modalData.nicInfo;
    this.method = this.nicInfo.method;

    this.nicForm = this.formBuilder.group({
      ipAddress: [
        {
          value: this.getIpFromIpWithPrefix(this.nicInfo.ip_address),
          disabled: (this.method === 'auto')
        },
        [ Validators.required, this.ipAddressOrEmptyValidator ]
      ],
      subnetMask: [
        {
          value: this.getSubnetMaskFromIpWithPrefix(this.nicInfo.ip_address),
          disabled: (this.method === 'auto')
        },
        [ Validators.required, this.ipAddressOrEmptyValidator ]
      ],
      gatewayAddress: [
        {
          value: this.nicInfo.gateway || '',
          disabled: (this.method === 'auto')
        },
        (this.nicName === 'customer') ?
          [ Validators.required, this.ipAddressOrEmptyValidator ] :
          [ this.ipAddressOrEmptyValidator ]
      ],
      dnsAddress: [
        {
          value: Utils.getSanitizedDnsAddress(this.nicInfo.dns),
          disabled: (this.method === 'auto')
        },
        (this.nicName === 'customer') ?
          [ Validators.required, this.ipAddressOrEmptyValidator ] :
          [ this.ipAddressOrEmptyValidator ]
      ],
      reason: [
        [Validators.required]
      ]
    });
  }

  updateFormEnabled(method) {
    if (method === 'manual') {
      this.nicForm.enable();
    }
    else {
      this.nicForm.disable();
      this.nicForm.controls['reason'].enable();
    }
  }

  getIpFromIpWithPrefix(ipWithPrefix) {
    if (!ipWithPrefix) {
      return '';
    }
    return ipWithPrefix.split('/')[0];
  }

  getSubnetMaskFromIpWithPrefix(ipWithPrefix) {
    try {
      const prefix = parseInt(ipWithPrefix.split('/')[1], 10);
      return IpSubnetCalculator.calculateSubnetMask('0.0.0.0', prefix).prefixMaskStr;
    }
    catch (ex) {
      return '';
    }
  }

  getPrefixFromSubnetMask(subnetMask) {
    try {
      return IpSubnetCalculator.calculateCIDRPrefix('0.0.0.0', subnetMask).prefixSize;
    }
    catch (ex) {
      return '';
    }
  }

  ipAddressOrEmptyValidator(control: FormControl): { [s: string]: boolean } {
    if (control.value === '') {
      return;
    }

    const result = IpSubnetCalculator.calculateCIDRPrefix(
      '0.0.0.0', control.value);

    if (!result) {
      return { ipAddress: true };
    }
  }

  cancel() {
    this.activeModal.dismiss();
  }

  getNewNicConfig() {
    if (this.method === 'auto') {
      return {
        nic_name: this.nicName,
        method: 'auto',
        manual_dns: '',
        manual_gateway: '',
        manual_ip_address: ''
      };
    }

    const prefix = this.getPrefixFromSubnetMask(this.nicForm.value.subnetMask);
    const ipAddressWithPrefix = `${this.nicForm.value.ipAddress}/${prefix}`;
    return {
      nic_name: this.nicName,
      method: this.method,
      manual_dns: this.nicForm.value.dnsAddress,
      manual_gateway: this.nicForm.value.gatewayAddress,
      manual_ip_address: ipAddressWithPrefix
    };
  }

  save() {
    const newNicConfig = this.getNewNicConfig();
    this.activeModal.close({ newNicConfig: newNicConfig, userReason: this.nicForm.get('reason').value });
  }

  getFormErrors(fieldName) {
      const form = this.nicForm;
      return (
          form.controls[fieldName] &&
          form.controls[fieldName].errors
      ) || {};
  }

  getTitle() {
    if (this.nicName === 'customer') {
      return 'Configure Corporate NIC';
    }
    if (this.nicName === 'aic_instrument') {
      return 'Configure Instrument NIC';
    }
  }

  getGatewayAddressLabel() {
    if (this.nicName === 'customer') {
      return 'Gateway Address';
    }
    return 'Gateway Address (Not Recommended)';
  }

  getDnsAddressLabel() {
    if (this.nicName === 'customer') {
      return 'DNS Address';
    }
    return 'DNS Address (optional)';
  }

  formIsReady(reasonForm: FormGroup) {
    this.nicForm.setControl('reason', reasonForm.get('userReason'));
  }

  isApplyButtonDisabled() {
    return (
      this.nicForm.dirty &&
      this.nicForm.invalid
    );
  }

}
